import { useCallback, useEffect, useState } from 'react'

// The dates a page was left on (Timesheets' day, Wages' pay period, and the
// period Product Activity, Batches and Reports share), kept for the rest of the
// browser tab per sign-in, so a reload or a trip to another page lands on them
// again. They are kept only for the day they were picked on: on a new day the
// page opens on its defaults. `at` is when they were picked, or last confirmed
// from the "You're looking at…" prompt (StillLooking.tsx), which asks once
// they have been left off the defaults for an hour.

type Kept = { uid: string | null; day: string; v: unknown; at: number }

function readRaw(key: string, uid: string | undefined, today: string): Kept | null {
  try {
    const k = JSON.parse(sessionStorage.getItem(key) || 'null')
    return k && k.uid === (uid ?? null) && k.day === today && typeof k.at === 'number' ? k : null
  } catch {
    return null
  }
}

function write(key: string, k: Kept) {
  try {
    sessionStorage.setItem(key, JSON.stringify(k))
  } catch {
    /* storage blocked: the dates are just not kept */
  }
}

// What the page kept today, when it is still a value the page can open on.
export function readKept<T>(key: string, uid: string | undefined, today: string, valid: (v: unknown) => v is T): T | null {
  const k = readRaw(key, uid, today)
  return k && valid(k.v) ? k.v : null
}

// Keeps `v` as picked now, outside a page (tests set a page's dates with it).
export function keepKept(key: string, uid: string | undefined, today: string, v: unknown) {
  write(key, { uid: uid ?? null, day: today, v, at: Date.now() })
}

export function forgetKept(key: string) {
  try {
    sessionStorage.removeItem(key)
  } catch {
    /* storage blocked: nothing was kept */
  }
}

// Keeps `value` as the page's dates. The time they were picked carries over
// while they stay the same (another page on the same period, a reload), and
// starts again when they change.
export function useKeptDates(key: string, uid: string | undefined, today: string, value: unknown) {
  const json = JSON.stringify(value)
  const since = () => {
    const k = readRaw(key, uid, today)
    return k && JSON.stringify(k.v) === json ? k.at : Date.now()
  }
  const [at, setAt] = useState(since)
  useEffect(() => {
    const t = since()
    write(key, { uid: uid ?? null, day: today, v: JSON.parse(json), at: t })
    setAt(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, uid, today, json])
  const confirm = useCallback(() => {
    const t = Date.now()
    write(key, { uid: uid ?? null, day: today, v: JSON.parse(json), at: t })
    setAt(t)
  }, [key, uid, today, json])
  return { at, confirm }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const nth = (d: number) => `${d}${d % 10 === 1 && d !== 11 ? 'st' : d % 10 === 2 && d !== 12 ? 'nd' : d % 10 === 3 && d !== 13 ? 'rd' : 'th'}`

// "28th Oct", "22nd – 25th Oct", "30th Sep – 2nd Oct", with the year when it
// is not this year's.
export function datesLabel(from: string, to: string, today: string): string {
  const [fy, fm, fd] = from.split('-').map(Number)
  const [ty, tm, td] = to.split('-').map(Number)
  const year = (y: number) => (String(y) === today.slice(0, 4) ? '' : ` ${y}`)
  const end = `${nth(td)} ${MONTHS[tm - 1]}${year(ty)}`
  if (from === to) return end
  const start = fy !== ty ? `${nth(fd)} ${MONTHS[fm - 1]}${year(fy)}` : fm !== tm ? `${nth(fd)} ${MONTHS[fm - 1]}` : nth(fd)
  return `${start} – ${end}`
}
